import React from 'react'
import { 
  Box, 
  Typography, 
  Paper, 
  Container, 
  IconButton, 
  Divider,
  useTheme
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

const PrivacyPolicy = ({ onClose }) => {
  const theme = useTheme()
  
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1300,
        overflow: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative',
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <IconButton onClick={onClose} aria-label="stäng">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Integritetspolicy
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Giltig från och med: 1 februari 2025
          </Typography>
          
          <Typography paragraph>
            Vi, NOS Technology AB, är engagerade i att skydda din integritet och personliga data. 
            Denna integritetspolicy beskriver hur vi samlar in, använder och lagrar din information 
            när du besöker en av våra hemsidor.
          </Typography>
          
          <Typography paragraph>
            Genom att skicka in din information via hemsidan samtycker du till villkoren i denna integritetspolicy.
          </Typography>
          
          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight="bold">
            Personuppgiftsansvarig och kontaktuppgifter
          </Typography>
          
          <Typography paragraph>
            För personuppgifter som du tillhandahåller och teknisk information som samlas in via cookies 
            och liknande teknologier är vi, NOS Technology AB, ansvariga.
          </Typography>
          
          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li" paragraph>
              <strong>Företagsnamn:</strong> NOS Technology AB
            </Typography>
            <Typography component="li" paragraph>
              <strong>Email:</strong> info@nostechnology.com
            </Typography>
          </Box>
          
          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight="bold">
            Vilka personuppgifter vi samlar in
          </Typography>
          
          <Typography paragraph>
            Vi kan samla in och behandla följande information:
          </Typography>
          
          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li" paragraph>
              <strong>Kontaktuppgifter:</strong> Namn, e-postadress, telefonnummer och annan information 
              du tillhandahåller via formulären på hemsidan.
            </Typography>
            <Typography component="li" paragraph>
              <strong>Teknisk information:</strong> IP-adress, webbläsartyp, enhetsinformation och annan 
              teknisk data som samlas in via cookies och liknande teknologier.
            </Typography>
            <Typography component="li" paragraph>
              <strong>Övriga uppgifter:</strong> All övrig information du väljer att lämna (t.ex. kommentarer eller frågor).
            </Typography>
          </Box>
          
          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight="bold">
            Hur vi använder din information
          </Typography>
          
          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li" paragraph>
              För att svara på dina förfrågningar och ge kundsupport.
            </Typography>
            <Typography component="li" paragraph>
              För att analysera webbtrafik och förbättra funktionaliteten på hemsidan.
            </Typography>
            <Typography component="li" paragraph>
              För att säkerställa hemsidans tekniska prestanda och säkerhet.
            </Typography>
          </Box>
          
          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight="bold">
            Varför vi använder din information
          </Typography>
          
          <Typography paragraph>
            Vi använder din information av följande huvudsakliga skäl:
          </Typography>
          
          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li" paragraph>
              <strong>Ditt samtycke:</strong> När du aktivt väljer att ge oss din information, till exempel genom att fylla i ett formulär.
            </Typography>
            <Typography component="li" paragraph>
              <strong>Förbättringar:</strong> För att förbättra vår webbplats och tjänster så länge det inte går ut över dina rättigheter.
            </Typography>
            <Typography component="li" paragraph>
              <strong>Lagliga krav:</strong> När vi måste följa lagar och regler.
            </Typography>
          </Box>
          
          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight="bold">
            Hur vi delar din data
          </Typography>
          
          <Typography paragraph>
            Vi säljer eller hyr aldrig ut dina personuppgifter. Vi kan dock dela din information med:
          </Typography>
          
          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li" paragraph>
              <strong>Tjänsteleverantörer:</strong> Tredjepartsleverantörer (till exempel verktyg för e-postmarknadsföring) 
              som hjälper oss att hantera teknisk data.
            </Typography>
            <Typography component="li" paragraph>
              <strong>Myndigheter:</strong> Om det krävs för att uppfylla lagliga skyldigheter.
            </Typography>
          </Box>
          
          <Typography paragraph>
            Alla tredjepartsleverantörer vi samarbetar med är bundna av strikta sekretessavtal och dataskyddsregler.
          </Typography>
          
          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight="bold">
            Lagring och radering av data
          </Typography>
          
          <Typography paragraph>
            Vi lagrar personuppgifter på ett säkert sätt och endast så länge det är nödvändigt för de ändamål 
            som beskrivs i denna policy eller för att uppfylla lagkrav.
          </Typography>
          
          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight="bold">
            Dina rättigheter (GDPR)
          </Typography>
          
          <Typography paragraph>
            Som individ i Sverige har du vissa rättigheter enligt GDPR, såsom att bli bortglömd och få dina 
            personuppgifter raderade. För att utöva dina rättigheter, vänligen kontakta oss.
          </Typography>
          
          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight="bold">
            Cookies och spårning
          </Typography>
          
          <Typography paragraph>
            Vi använder cookies och liknande tekniker för att förbättra din upplevelse på webbplatsen och för marknadsföring. 
            Dessa tekniker hjälper oss att spåra användarbeteende och visa relevanta annonser.
          </Typography>
          
          <Typography paragraph>
            <strong>Specifikt används:</strong>
          </Typography>
          
          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li" paragraph>
              <strong>Meta (Facebook & Instagram):</strong> För att spåra användare och analysera webbplatsens användning. 
              Detta gör det möjligt att visa anpassade annonser på Facebook och Instagram.
            </Typography>
            <Typography component="li" paragraph>
              <strong>Google Ads:</strong> För att spåra användare och analysera webbplatsens användning. 
              Detta gör det möjligt att visa anpassade annonser via Google Ads-nätverket.
            </Typography>
          </Box>
          
          <Typography paragraph>
            Du kan välja att inaktivera cookies i din webbläsare, men det kan påverka webbplatsens funktionalitet.
          </Typography>
          
          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight="bold">
            Ändringar i denna integritetspolicy
          </Typography>
          
          <Typography paragraph>
            Vi förbehåller oss rätten att uppdatera denna integritetspolicy vid behov. Datumet för den senaste 
            ändringen anges högst upp i denna policy. Vi rekommenderar att du regelbundet granskar denna policy 
            för att hålla dig informerad om hur vi skyddar din information.
          </Typography>
        </Paper>
      </Container>
    </Box>
  )
}

export default PrivacyPolicy 